import React from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HtmlRenderer } from './HtmlRenderer';
import { AppColors } from '../utils/utils';
import { SectionHeader } from './SectionHeader';

interface SectionViewProps {
  idx: number;
  label: string;
  title: string;
  subtitle: string;
  features: string[];
  footerText: string;
  imagePath: string;
  imageBGPath?: string;
  imageAlign?: 'start' | 'center' | 'end';
  isForFeature?: boolean;
}

export const SectionView: React.FC<SectionViewProps> = ({
  idx,
  label,
  title,
  subtitle,
  features,
  footerText,
  imagePath,
  imageBGPath,
  imageAlign,
  isForFeature,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  const isImageOnRight = idx % 2 !== 0;

  const gridSize = { xs: 12, sm: 5.5, md: 5.5 };

  const getScreenshotView = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: imageAlign ?? 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {imageBGPath && (
        <Box
          component='img'
          src={imageBGPath}
          sx={{
            position: 'absolute',
            display: 'block',
            right: {
              sm: 0,
              md: isImageOnRight ? (imageAlign === 'end' ? '-60%' : '-30%') : 0,
            },
            left: { sm: 0, md: isImageOnRight ? 0 : '-30%' },
            height: isMobile ? '100%' : '100%',
            width: isMobile ? '100%' : 'auto',
            // rotate: idx === 1 ? '30deg' : idx === 2 ? '-150deg' : '0deg',
          }}
        />
      )}

      <Box
        component='img'
        src={imagePath}
        width={{ xs: '80%', sm: '100%', md: '70%' }}
        height='auto'
        sx={{ position: 'relative' }}
      />
    </Box>
  );

  const getFeatureListView = (list: string[], titleTextColor?: string) => (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='center'
      alignItems='start'
    >
      {list.map((item, index) => (
        <Stack key={index} direction='row' alignItems='start' py={1}>
          <Box
            component='img'
            src='/static/images/ic_ai_arrow.svg'
            height='24px'
            width='24px'
          />
          <Typography
            sx={{
              pl: 1,
              color: titleTextColor ?? AppColors.grayThreeColor,
              fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
              '& span': {
                background: AppColors.aiGradient,
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontWeight: 'bold',
              },
            }}
          >
            <HtmlRenderer htmlString={item} />
          </Typography>
        </Stack>
      ))}
    </Box>
  );

  return (
    <Box
      py={isForFeature ? 0 : 3}
      my={3}
      sx={{
        background: isImageOnRight
          ? AppColors.aiGradientCrossTransparent
          : null,
      }}
    >
      <Grid
        container
        gap={{ sx: 0, sm: 5, md: 5 }}
        direction='row'
        // justifyContent='space-evenly'
        // alignItems={isForFeature ? 'end' : 'center'}
        alignItems={'center'}
        py={isForFeature ? 0 : 3}
        // px={isForFeature ? 0 : '6%'}
        paddingLeft={{ xs: 2, sm: '5%', md: '8%', lg: '15%' }}
        paddingRight={
          isForFeature ? 0 : { xs: 2, sm: '5%', md: '8%', lg: '15%' }
        }
        sx={{
          background: !isForFeature
            ? !isImageOnRight
              ? `url('/static/images/img_ai_wave_background.svg')`
              : `url('/static/images/img_ai_circuit_background_small.png')`
            : null,
          backgroundPosition: isImageOnRight
            ? `${isMobile ? 'center' : 'top'} right`
            : `${isMobile ? 'center' : 'top'} left`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: {
            xs: 'auto 30%',
            sm: isImageOnRight ? 'auto' : 'contain',
            md: isImageOnRight ? 'auto' : 'contain',
            lg: 'auto',
          },
        }}
      >
        {!isMobile && !isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}

        <Grid size={gridSize}>
          <Box
            width={isForFeature ? { xs: '100%', sm: '60%', md: '60%' } : '100%'}
            // paddingLeft={isMobile ? 2 : '10%'}
            // paddingRight={isMobile ? 2 : '10%'}
            py={isForFeature ? 3 : 0}
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems={{ xs: 'center', sm: 'center', md: 'start' }}
          >
            <SectionHeader text={label} />

            <Typography
              component='p'
              sx={{
                fontSize: 'clamp(1.25rem, 2vw, 2rem)',
                fontWeight: 600,
                color: AppColors.textBlackColor,
                textAlign: isMobile ? 'center' : 'start',
                my: 1,
                '& span': {
                  background: AppColors.textGradient,
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                },
              }}
            >
              {isForFeature ? (
                title
              ) : (
                <>
                  {`Step ${idx + 1}: `} <span>{title}</span>
                </>
              )}
            </Typography>

            {subtitle && (
              <Typography
                component='p'
                textAlign={{ xs: 'center', sm: 'center', md: 'start' }}
                fontWeight={400}
                py={1}
                color='#6B727A'
              >
                <HtmlRenderer htmlString={subtitle} />
              </Typography>
            )}

            {isMobile && <Box py={2}>{getScreenshotView()}</Box>}

            {getFeatureListView(features)}

            {footerText && (
              <Typography
                sx={{ py: 1, color: AppColors.redColor, fontStyle: 'italic' }}
              >
                {footerText}
              </Typography>
            )}

            {/* <GradientButton sx={{ mt: 2 }} onClick={() => {}}>
            {isForFeature ? 'Learn More' : 'Get Started'}
          </GradientButton> */}
          </Box>
        </Grid>

        {!isMobile && isImageOnRight && (
          <Grid size={gridSize}>{getScreenshotView()}</Grid>
        )}
      </Grid>
    </Box>
  );
};